import React from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { RadioOption } from '../../shared';
import type { LeadSelectionStepProps } from '../types';

/**
 * Lead selection step for campaign creation.
 * Allows users to select all, filtered, specific rows, or first N leads.
 */
export const LeadSelectionStep: React.FC<LeadSelectionStepProps> = ({
  totalLeadCount,
  filteredLeadCount,
  selectedLeadCount,
  hasActiveFilters,
  selectionMode,
  customCountInput,
  error,
  maxLeadCount,
  onSelectionChange,
  onCustomCountChange,
  onProceed,
}) => {
  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');
  const effectiveMax = maxLeadCount ?? totalLeadCount;
  const isLimited = maxLeadCount !== undefined && maxLeadCount < totalLeadCount;

  // Helper to get display count (capped to max)
  const getDisplayCount = (count: number) => {
    const capped = Math.min(count, effectiveMax);
    return capped.toLocaleString();
  };

  return (
    <div className="space-y-1.5 mt-3">
      {isLimited && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-[11px] text-amber-700">
            A max of {maxLeadCount} emails allowed in one go to avoid spamming.
          </p>
        </div>
      )}

      {/* All leads */}
      <RadioOption
        selected={selectionMode === 'all'}
        onClick={() => onSelectionChange('all')}
        rightText={isLimited && totalLeadCount > effectiveMax
          ? `${getDisplayCount(totalLeadCount)} of ${totalLeadCount.toLocaleString()}`
          : totalLeadCount.toLocaleString()
        }
      >
        {isLimited ? `First ${effectiveMax} leads` : 'All leads'}
      </RadioOption>

      {/* Filtered leads */}
      {hasActiveFilters && filteredLeadCount !== undefined && filteredLeadCount !== totalLeadCount && (
        <RadioOption
          selected={selectionMode === 'filtered'}
          onClick={() => onSelectionChange('filtered')}
          rightText={isLimited && filteredLeadCount > effectiveMax
            ? `${getDisplayCount(filteredLeadCount)} of ${filteredLeadCount.toLocaleString()}`
            : filteredLeadCount.toLocaleString()
          }
        >
          {isLimited && filteredLeadCount > effectiveMax ? `First ${effectiveMax} filtered` : 'Filtered leads'}
        </RadioOption>
      )}

      {/* Select specific rows */}
      <RadioOption
        selected={selectionMode === 'selected'}
        onClick={() => onSelectionChange('selected')}
        rightText={
          selectedLeadCount && selectedLeadCount > 0
            ? isLimited && selectedLeadCount > effectiveMax
              ? `${getDisplayCount(selectedLeadCount)} of ${selectedLeadCount.toLocaleString()}`
              : `${selectedLeadCount.toLocaleString()} selected`
            : 'click rows in table'
        }
      >
        Select specific rows
      </RadioOption>

      {selectionMode === 'selected' && (
        <p className="text-[11px] text-gray-400 pl-6 mt-1">
          Click rows in the table to select them. Use {isMac ? '⌘' : 'Ctrl'}+click to add/remove individual rows.
          {isLimited && selectedLeadCount && selectedLeadCount > effectiveMax && (
            <span className="text-amber-600"> Only first {effectiveMax} will be sent.</span>
          )}
        </p>
      )}

      {/* First N leads - only show when not limited (since "All leads" becomes "First N" when limited) */}
      {!isLimited && (
        <div
          onClick={() => onSelectionChange('custom')}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all cursor-pointer ${
            selectionMode === 'custom'
              ? 'border-gray-800 bg-gray-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selectionMode === 'custom' ? 'border-gray-800 bg-gray-800' : 'border-gray-300'
            }`}
          >
            {selectionMode === 'custom' && <Check className="w-2.5 h-2.5 text-white" />}
          </div>
          <span className="text-[13px] text-gray-800">First</span>
          <input
            type="number"
            value={customCountInput}
            onClick={(e) => {
              e.stopPropagation();
              onSelectionChange('custom');
            }}
            onChange={(e) => onCustomCountChange(e.target.value)}
            min={1}
            max={totalLeadCount}
            className="w-14 px-2 py-0.5 text-[13px] text-center bg-white border border-gray-200 rounded focus:outline-none focus:border-gray-400"
          />
          <span className="text-[13px] text-gray-800">leads</span>
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-1.5 p-2 bg-red-50 border border-red-100 rounded-lg">
          <AlertCircle className="w-3 h-3 text-red-500" />
          <span className="text-[11px] text-red-600">{error}</span>
        </div>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={onProceed}
          disabled={selectionMode === 'selected' && (!selectedLeadCount || selectedLeadCount === 0)}
          className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

LeadSelectionStep.displayName = 'LeadSelectionStep';
